import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../../../auth/hooks/useAuth";

/**
 * AuthGuard component for POS access
 * Checks if the user is authenticated and has POS access
 */
export const AuthGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  // Use a timeout to prevent indefinite loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsChecking(false);
    }, 3000); // 3 seconds timeout for initial check

    return () => clearTimeout(timer);
  }, []);

  // Clear session and redirect if the user is not authenticated or doesn't have POS access
  useEffect(() => {
    const clearSessionStorage = () => {
      localStorage.removeItem("pos_session_id");
      localStorage.removeItem("pos_session_data");
    };

    if (!loading && !isChecking) {
      if (!isAuthenticated) {
        // Not authenticated, redirect to login
        console.log("User not authenticated, redirecting to login");
        clearSessionStorage();
        navigate("/auth/cashier-login", {
          state: { message: "Please log in to access the POS system" },
        });
      } else if (user && !user.is_pos_user) {
        // User doesn't have POS access
        console.log("User doesn't have POS access, redirecting to dashboard");
        clearSessionStorage();
        navigate("/dashboard", {
          state: {
            error:
              "You don't have access to the POS system. Please contact your administrator.",
          },
        });
      }
    }
  }, [isAuthenticated, loading, user, navigate, isChecking]);

  // Show loading state while checking authentication
  if (loading || isChecking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <CircularProgress size={40} />
        <Typography variant="h6" style={{ marginTop: 16 }}>
          Verifying your access...
        </Typography>
      </Box>
    );
  }

  // If authenticated and has POS access, render children
  return isAuthenticated && user?.is_pos_user ? (
    <>{children}</>
  ) : (
    // This should not be reached due to the redirect in useEffect,
    // but included as a fallback
    <Navigate to="/auth/cashier-login" />
  );
};
